import { BigNumber } from 'bignumber.js';

import type {
  AssetGroupId,
  AssetMetadataV2,
  ChainAddressId,
  LstAssetMetadata,
  PositionType,
} from '@infinex/asset-config';
import {
  isValidChainAddress,
  KNOWN_POSITION_PROTOCOLS,
  makeChainAddressId,
  parseChainAddress,
  POSITION_TYPES,
  USDC_SOLANA,
} from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type {
  AssetRate,
  ChainBalancesDataset,
  CollectiblesDataset,
  InxAllocationSource,
  InxDataset,
  PerpsBalance,
  PerpsDataset,
} from '@infinex/types';
import {
  EMPTY_COLLECTOR_CRYPT_POINTS_SUMMARY,
  EMPTY_COLLECT_SHIPPING_REDEMPTIONS_SUMMARY,
  EMPTY_GACHAS_SUMMARY,
} from '@infinex/types';

import {
  selectSpotHoldingDisplayItems,
  selectSpotHoldings,
  selectUnifiedMarginOverlap,
} from '../../../../api/perps/selectSpotHoldings';
import { HYPERLIQUID_SUPPORTED_ASSET } from '../../../../lib/hyperliquid/constants';
import type {
  ChainAssetBalance,
  ChainPositionChainAssetBalance,
  RateData,
} from '../../balance';
import { calculateBalanceChange } from '../../utils/calculateBalanceChange';

function indexMetadataV2ByChainAddress(
  metadata: AssetMetadataV2[]
): Map<ChainAddressId, AssetMetadataV2> {
  const map = new Map<ChainAddressId, AssetMetadataV2>();
  for (const item of metadata) {
    for (const member of Object.values(item.members)) {
      if (!member) continue;
      map.set(makeChainAddressId(member.chain, member.address), item);
    }
  }
  return map;
}

function getAssetIdFromMetadataV2(metadata: AssetMetadataV2) {
  return 'assetId' in metadata.groupId ? metadata.groupId.assetId : undefined;
}

function getMetadataV2Member(
  metadata: AssetMetadataV2 | undefined,
  chain: ChainKey
) {
  return metadata?.members[chain];
}

const EMPTY_COLLECTIBLES_DATASET: CollectiblesDataset = {
  generatedAt: 0,
  cards: [],
  totalValue: '0',
  pricedCardCount: 0,
  unpricedCardCount: 0,
  gachas: EMPTY_GACHAS_SUMMARY,
  collectorCryptPoints: EMPTY_COLLECTOR_CRYPT_POINTS_SUMMARY,
  shipping: EMPTY_COLLECT_SHIPPING_REDEMPTIONS_SUMMARY,
  openOffers: [],
};

/**
 * Precomputed balances for unified balances calculations.
 */
export type PrecomputedBalances = {
  /**
   * Balances keyed by wallet ChainAddressId: makeChainAddressId(walletChain, walletAddress)
   * Value: All asset balances for that specific wallet address
   */
  balancesByWalletAddress: Record<ChainAddressId, ChainAssetBalance[]>;

  /**
   * Original dataset timestamps for cache invalidation
   */
  datasetTimestamps: {
    chainBalances: number;
    inx: number;
    perps: number;
    collectibles: number;
  };
};

/**
 * Precompute values for unifiedBalances without any filtering by account addresses
 */
export function precomputeBalances(
  chainBalancesDataset: ChainBalancesDataset,
  inxDataset: InxDataset,
  perpsDataset: PerpsDataset,
  inxPrice: number | null,
  collectiblesDataset: CollectiblesDataset = EMPTY_COLLECTIBLES_DATASET
): PrecomputedBalances {
  const balancesByWalletAddress: Record<ChainAddressId, ChainAssetBalance[]> =
    {};

  for (const balance of mapChainBalancesDatasetToBalances(
    chainBalancesDataset
  )) {
    const walletKey = makeChainAddressId(balance.chain, balance.walletAddress);
    if (!balancesByWalletAddress[walletKey]) {
      balancesByWalletAddress[walletKey] = [];
    }
    balancesByWalletAddress[walletKey].push(balance);
  }

  for (const balance of mapPerpsDatasetToPositions(perpsDataset)) {
    const walletKey = makeChainAddressId(balance.chain, balance.walletAddress);
    if (!balancesByWalletAddress[walletKey]) {
      balancesByWalletAddress[walletKey] = [];
    }
    balancesByWalletAddress[walletKey].push(balance);
  }

  const inxAddresses = new Set(
    inxDataset.allocations.map((allocation) =>
      parseChainAddress(allocation.walletAddress)
    )
  );

  for (const balance of mapInxDatasetAllocations(
    inxDataset,
    inxAddresses,
    inxPrice
  )) {
    const walletKey = makeChainAddressId(balance.chain, balance.walletAddress);
    if (!balancesByWalletAddress[walletKey]) {
      balancesByWalletAddress[walletKey] = [];
    }
    balancesByWalletAddress[walletKey].push(balance);
  }

  for (const balance of mapCollectiblesDatasetCards(collectiblesDataset)) {
    const walletKey = makeChainAddressId(balance.chain, balance.walletAddress);
    if (!balancesByWalletAddress[walletKey]) {
      balancesByWalletAddress[walletKey] = [];
    }
    balancesByWalletAddress[walletKey].push(balance);
  }

  const openOffersPositions = mapCollectiblesOpenOffersPositions(
    collectiblesDataset,
    chainBalancesDataset
  );
  for (const openOffersPosition of openOffersPositions) {
    const walletKey = makeChainAddressId(
      openOffersPosition.chain,
      openOffersPosition.walletAddress
    );
    if (!balancesByWalletAddress[walletKey]) {
      balancesByWalletAddress[walletKey] = [];
    }
    balancesByWalletAddress[walletKey].push(openOffersPosition);
  }

  return {
    balancesByWalletAddress,
    datasetTimestamps: {
      chainBalances: chainBalancesDataset.generatedAt,
      inx: inxDataset.meta?.lastSyncedAt ?? 0,
      perps: perpsDataset.generatedAt,
      collectibles: collectiblesDataset.generatedAt,
    },
  };
}

export function mapChainBalancesDatasetToBalances(
  dataset: ChainBalancesDataset,
  applicableAddresses?: Set<ChainAddressId>
): ChainAssetBalance[] {
  const metadataLookup = indexMetadataV2ByChainAddress(
    dataset.metadataV2 ?? []
  );

  const lstMetadataLookup = new Map<ChainAddressId, LstAssetMetadata>();
  if (dataset.lstMetadata) {
    for (const m of dataset.lstMetadata) {
      lstMetadataLookup.set(makeChainAddressId(m.chain, m.address), m);
    }
  }

  const priceLookup = new Map<ChainAddressId, AssetRate>();
  for (const p of dataset.prices) {
    priceLookup.set(makeChainAddressId(p.chain, p.address), p);
  }

  const applicableBalances = applicableAddresses
    ? dataset.balances.filter((balance) =>
        applicableAddresses.has(
          makeChainAddressId(balance.chain, balance.walletAddress)
        )
      )
    : dataset.balances;

  return applicableBalances
    .map((balance): ChainAssetBalance | null => {
      const id = makeChainAddressId(balance.chain, balance.assetAddress);
      const metadata = metadataLookup.get(id);
      const member = metadata?.members[balance.chain];
      if (!metadata || !member) return null;

      const price = priceLookup.get(id);

      const rawBalance = BigNumber(balance.rawBalance);
      const decimalBalance = rawBalance.shiftedBy(-member.decimals);

      const groupId: AssetGroupId = metadata.groupId;
      const assetId = getAssetIdFromMetadataV2(metadata);

      const baseBalance = {
        walletAddress: balance.walletAddress,
        chain: balance.chain,
        assetAddress: balance.assetAddress,
        id,
        assetId: assetId,
        groupId,
        name: metadata.name,
        symbol: metadata.symbol,
        imgUrl: metadata.imgUrl,
        decimals: member.decimals,
        rawBalance,
        decimalBalance,
        isOfficial: metadata.isOfficial,
        lstAssetMetadata: lstMetadataLookup.get(id) ?? null,
        ...computeRateData(price, decimalBalance),
      };

      if (balance.type === 'token') {
        return { ...baseBalance, type: 'token' as const };
      } else if (balance.type === 'nft') {
        return {
          ...baseBalance,
          type: 'nft' as const,
          isPatron: balance.isPatron ?? false,
          nftData: balance.nftData ?? [],
        };
      } else {
        // positionData comes straight from the server dataset: a protocol id
        // or position type added on the backend before this client updates is
        // unknown to the local registries and would crash the registry lookups
        // downstream (KNOWN_POSITION_PROTOCOLS[id].name,
        // getPositionTypeMultiplier). Skip such positions.
        if (
          !(balance.positionData.protocolId in KNOWN_POSITION_PROTOCOLS) ||
          !POSITION_TYPES.includes(balance.positionData.positionType)
        ) {
          return null;
        }
        return {
          ...baseBalance,
          type: 'position' as const,
          positionData: balance.positionData,
        };
      }
    })
    .filter((b): b is ChainAssetBalance => b !== null);
}

function mapPerpsDatasetToPositions(
  dataset: PerpsDataset,
  applicableWalletAddressIds?: Set<ChainAddressId>
): ChainPositionChainAssetBalance[] {
  // Perp-margin collateral positions are derived from the read-model balances,
  // which carry the routing chain + collateral asset address per provider.
  const perpMarginBalances = (dataset.balances ?? []).filter(
    (
      balance
    ): balance is PerpsBalance & { chain: ChainKey; assetAddress: string } =>
      // Optional/cleared address fields are normalised to '' in this repo,
      // so reject empty strings as well as undefined before these values
      // reach makeChainAddressId.
      balance.balanceKind === 'perp_margin' &&
      !!balance.chain &&
      !!balance.assetAddress
  );

  // Build lookups for metadata and prices
  const metadataLookup = indexMetadataV2ByChainAddress(
    dataset.metadataV2 ?? []
  );
  const priceLookup = new Map(
    dataset.prices.map((p) => [makeChainAddressId(p.chain, p.address), p])
  );

  const applicableBalances = applicableWalletAddressIds
    ? perpMarginBalances.filter((balance) =>
        applicableWalletAddressIds.has(
          makeChainAddressId(balance.chain, balance.accountId)
        )
      )
    : perpMarginBalances;

  const spotPositions = mapSpotHoldingsToPositions({
    dataset,
    metadataLookup,
    priceLookup,
    applicableWalletAddressIds,
  });

  // Display-only rows (per-market perps + per-token spot). These render inside
  // the portfolio card but are excluded from the protocol total — the `perp`
  // and `spot` position types have a 0 multiplier, so the header stays equal to
  // the `collateral` equity emitted above/below.
  const metadataBySymbol = buildMetadataBySymbolIndex(dataset.metadataV2 ?? []);
  const perpDisplayPositions = mapPerpPositionsToDisplayPositions(
    dataset,
    metadataBySymbol
  );
  const spotDisplayPositions = mapSpotTokensToDisplayPositions(
    dataset,
    metadataBySymbol
  );

  return [
    ...spotPositions,
    ...perpDisplayPositions,
    ...spotDisplayPositions,
    ...applicableBalances.flatMap((balance) => {
      const { chain, assetAddress, accountId: walletAddress } = balance;
      const chainAddressId = makeChainAddressId(chain, assetAddress);
      const metadata = metadataLookup.get(chainAddressId);
      const member = metadata?.members[chain];
      const price = priceLookup.get(chainAddressId);
      const positionDetails = getPositionDetailsForProvider(balance.providerId);

      if (!metadata || !member || !positionDetails) {
        return [];
      }

      const decimalBalance = new BigNumber(balance.amount);

      if (!decimalBalance.isFinite() || decimalBalance.isZero()) {
        return [];
      }

      return [
        {
          // Required ID field
          id: chainAddressId,

          // Chain and address info
          chain,
          walletAddress,
          assetAddress,

          // Position-specific data
          type: 'position' as const,
          groupId: metadata.groupId,
          rawBalance: decimalBalance.shiftedBy(member.decimals),
          decimalBalance,
          lstAssetMetadata: null,

          // Metadata from perps dataset
          name: metadata.name,
          symbol: metadata.symbol,
          decimals: member.decimals,

          // Position details
          positionData: {
            positionType: 'collateral' as const,
            protocolId: positionDetails.protocolId,
            externalIntegrationId: positionDetails.externalIntegrationId,

            // Store the account value in receiptTokenAddress as JSON.
            receiptTokenAddress: JSON.stringify({
              accountValue: balance.amount,
            }),
            receiptTokenType: 'token' as const,
          },

          // Official list metadata
          isOfficial: metadata.isOfficial,
          assetId: getAssetIdFromMetadataV2(metadata),

          // Image URL from metadata
          imgUrl: metadata.imgUrl,

          // Rates from perps dataset
          ...computeRateData(price, decimalBalance),
        },
      ];
    }),
  ];
}

/**
 * Aggregate spot holdings (Hyperliquid spot trading balances) into one
 * collateral position per account, denominated in the provider's collateral
 * asset (USDC on Arbitrum) so the existing metadata/price plumbing values it
 * 1:1.
 *
 * The spot value is reduced by the unified-margin overlap before it becomes
 * a position: on a unified account, spot USDC held to back an open perp
 * position is the same dollars Hyperliquid reports as the perp clearing
 * house value (which the perp-margin position above already carries), so
 * adding raw spot on top would double count the position's margin. See
 * {@link selectUnifiedMarginOverlap}.
 *
 * Rows without a markPrice cannot be valued and contribute nothing here —
 * the /perps page still surfaces them as token amounts, so the portfolio
 * total intentionally undercounts an account holding unpriceable tokens
 * rather than fabricating a value. Hyperliquid is the only spot provider
 * today; other providers would need their own collateral denomination added
 * to {@link getPositionDetailsForProvider}-style routing.
 *
 * Because these are `type: 'position'` balances, spot value flows into the
 * portfolio header total via `totalUsdPositionBalance` and is hidden by the
 * header's show/hide-positions toggle together with perp margin.
 */
function mapSpotHoldingsToPositions(input: {
  dataset: PerpsDataset;
  metadataLookup: Map<ChainAddressId, AssetMetadataV2>;
  priceLookup: Map<ChainAddressId, AssetRate>;
  applicableWalletAddressIds?: Set<ChainAddressId>;
}): ChainPositionChainAssetBalance[] {
  const { dataset, metadataLookup, priceLookup, applicableWalletAddressIds } =
    input;

  // All Hyperliquid rows (perp margin + spot) per account: the overlap is a
  // per-account min() across both kinds, so the grouping must carry both.
  const hyperliquidBalancesByAccount = new Map<string, PerpsBalance[]>();
  let hasSpotRows = false;
  for (const balance of dataset.balances ?? []) {
    if (balance.providerId !== 'hyperliquid') continue;
    if (
      balance.productKind === 'spot' &&
      balance.balanceKind === 'spot_token'
    ) {
      hasSpotRows = true;
    }
    const rows = hyperliquidBalancesByAccount.get(balance.accountId);
    if (rows) {
      rows.push(balance);
    } else {
      hyperliquidBalancesByAccount.set(balance.accountId, [balance]);
    }
  }
  if (!hasSpotRows) return [];

  const { chain, address: assetAddress } = HYPERLIQUID_SUPPORTED_ASSET;
  const chainAddressId = makeChainAddressId(chain, assetAddress);
  const metadata = metadataLookup.get(chainAddressId);
  const price = priceLookup.get(chainAddressId);
  const positionDetails = getPositionDetailsForProvider('hyperliquid');
  const member = metadata?.members[chain];
  if (!metadata || !member || !positionDetails) return [];

  return Array.from(hyperliquidBalancesByAccount, ([accountId, rows]) => ({
    accountId,
    spotValue:
      selectSpotHoldings(rows).spotUsdValue - selectUnifiedMarginOverlap(rows),
  })).flatMap(({ accountId, spotValue }) => {
    if (
      applicableWalletAddressIds &&
      !applicableWalletAddressIds.has(makeChainAddressId(chain, accountId))
    ) {
      return [];
    }

    const decimalBalance = new BigNumber(spotValue);
    if (!decimalBalance.isFinite() || decimalBalance.isZero()) {
      return [];
    }

    return [
      {
        id: chainAddressId,

        chain,
        walletAddress: accountId,
        assetAddress,

        type: 'position' as const,
        groupId: metadata.groupId,
        rawBalance: decimalBalance.shiftedBy(member.decimals),
        decimalBalance,
        lstAssetMetadata: null,

        name: metadata.name,
        symbol: metadata.symbol,
        decimals: member.decimals,

        positionData: {
          positionType: 'collateral' as const,
          protocolId: positionDetails.protocolId,
          externalIntegrationId: positionDetails.externalIntegrationId,
          receiptTokenAddress: JSON.stringify({
            spotUsdValue: String(spotValue),
          }),
          receiptTokenType: 'token' as const,
        },

        isOfficial: metadata.isOfficial,
        assetId: getAssetIdFromMetadataV2(metadata),

        imgUrl: metadata.imgUrl,

        ...computeRateData(price, decimalBalance),
      },
    ];
  });
}

/**
 * Internal chain key for perp/spot display rows. These positions are not real
 * on-chain assets, so the value is used only as a synthetic grouping/dedup key
 * (never surfaced — the perps-aware row hides the network badge). Arbitrum is
 * Hyperliquid's collateral routing chain.
 */
const PERPS_DISPLAY_CHAIN = 'arbitrum' as const satisfies ChainKey;

/**
 * Index provider metadata by upper-cased symbol so coin strings (e.g. "BTC",
 * "HYPE") on perp positions and spot balances can resolve a name/icon. The
 * perps dataset keys metadata by (chain, address); display rows only have a
 * coin symbol. First entry wins; rows with no match fall back to the raw coin.
 */
function buildMetadataBySymbolIndex(
  metadata: AssetMetadataV2[]
): Map<string, AssetMetadataV2> {
  const map = new Map<string, AssetMetadataV2>();
  for (const m of metadata) {
    const key = m.symbol?.toUpperCase();
    if (!key || map.has(key)) continue;
    map.set(key, m);
  }
  return map;
}

/**
 * Per-market perp positions (Hyperliquid, Lighter, …) as display-only rows.
 * The row's headline USD is the position notional (`positionValue`); side and
 * leverage and unrealized PnL ride along in {@link PerpsPositionMeta} for the
 * perps-aware row. Excluded from protocol totals via the `perp` multiplier.
 *
 * Same-coin positions across multiple accounts group into one row (size and
 * notional sum); the side/leverage/PnL tag reflects the first member, which is
 * the common single-account case.
 */
function mapPerpPositionsToDisplayPositions(
  dataset: PerpsDataset,
  metadataBySymbol: Map<string, AssetMetadataV2>
): ChainPositionChainAssetBalance[] {
  return (dataset.positions ?? []).flatMap((position) => {
    const positionDetails = getPositionDetailsForProvider(position.exchange);
    if (!positionDetails) return [];

    const size = new BigNumber(position.szi);
    if (!size.isFinite() || size.isZero()) return [];

    const absSize = size.abs();
    const meta = metadataBySymbol.get(position.coin.toUpperCase());
    // Key by wallet too so same-coin positions across accounts stay distinct —
    // merging them would aggregate totals but keep one member's side/leverage/PnL.
    const syntheticAddress = `${position.exchange}-perp-${position.walletAddress}-${position.coin}`;
    const notionalUsd = Number.isFinite(position.positionValue)
      ? position.positionValue
      : null;
    const unrealizedPnl = Number(position.unrealizedPnl);

    return [
      {
        // Synthetic ChainAddressId: perp markets are not on-chain assets, so
        // bypass makeChainAddressId (which validates the address). Only used as
        // a unique grouping/dedup key.
        id: `${PERPS_DISPLAY_CHAIN}_${syntheticAddress}` as ChainAddressId,
        chain: PERPS_DISPLAY_CHAIN,
        walletAddress: position.walletAddress,
        assetAddress: syntheticAddress,
        type: 'position' as const,
        groupId: { chain: PERPS_DISPLAY_CHAIN, address: syntheticAddress },
        rawBalance: absSize,
        decimalBalance: absSize,
        lstAssetMetadata: null,
        name: meta?.name ?? position.coin,
        symbol: meta?.symbol ?? position.coin,
        decimals: getMetadataV2Member(meta, PERPS_DISPLAY_CHAIN)?.decimals ?? 0,
        imgUrl: meta?.imgUrl ?? '',
        isOfficial: false,
        positionData: {
          positionType: 'perp' as const,
          protocolId: positionDetails.protocolId,
          externalIntegrationId: positionDetails.externalIntegrationId,
        },
        perpsMeta: {
          exchange: position.exchange,
          coin: position.coin,
          productKind: 'perp' as const,
          side: size.isNegative() ? ('short' as const) : ('long' as const),
          leverage: position.leverage?.value,
          unrealizedPnlUsd: Number.isFinite(unrealizedPnl)
            ? unrealizedPnl
            : undefined,
        },
        balanceUsd: notionalUsd,
        balance24hChangeUsd: null,
        rateUsd: null,
        rateUsdChange24h: null,
        rateLastUpdated: null,
        isRateLoading: false as const,
      },
    ];
  });
}

/**
 * Per-token Hyperliquid spot holdings as display-only rows (Hyperliquid is the
 * only spot provider today). Reuses {@link selectSpotHoldingDisplayItems} so
 * the values match the /perps spot list. Unpriced tokens carry a null USD value
 * and render the token amount only. Excluded from protocol totals via the
 * `spot` multiplier; the aggregated spot value still flows through the separate
 * `collateral` position emitted by {@link mapSpotHoldingsToPositions}.
 */
function mapSpotTokensToDisplayPositions(
  dataset: PerpsDataset,
  metadataBySymbol: Map<string, AssetMetadataV2>
): ChainPositionChainAssetBalance[] {
  const positionDetails = getPositionDetailsForProvider('hyperliquid');
  if (!positionDetails) return [];

  const hyperliquidBalances = (dataset.balances ?? []).filter(
    (balance) => balance.providerId === 'hyperliquid'
  );

  return selectSpotHoldingDisplayItems(hyperliquidBalances).flatMap((item) => {
    // Hide the USDC collateral from the spot list (it's surfaced via the
    // aggregated collateral position that drives the total), matching the
    // /perps "Your spot balances" section.
    if (item.isUsdc) return [];

    const total = new BigNumber(item.total);
    if (!total.isFinite() || total.isZero()) return [];

    const meta = metadataBySymbol.get(item.asset.toUpperCase());
    // Key by account too so the same token across wallets stays distinct.
    const syntheticAddress = `hyperliquid-spot-${item.accountId}-${item.asset}`;
    const rateUsd =
      item.usdValue !== undefined && total.gt(0)
        ? item.usdValue / total.toNumber()
        : null;

    return [
      {
        // Synthetic ChainAddressId (see mapPerpPositionsToDisplayPositions).
        id: `${PERPS_DISPLAY_CHAIN}_${syntheticAddress}` as ChainAddressId,
        chain: PERPS_DISPLAY_CHAIN,
        walletAddress: item.accountId,
        assetAddress: syntheticAddress,
        type: 'position' as const,
        groupId: { chain: PERPS_DISPLAY_CHAIN, address: syntheticAddress },
        rawBalance: total,
        decimalBalance: total,
        lstAssetMetadata: null,
        name: meta?.name ?? item.asset,
        symbol: meta?.symbol ?? item.asset,
        decimals: getMetadataV2Member(meta, PERPS_DISPLAY_CHAIN)?.decimals ?? 0,
        imgUrl: meta?.imgUrl ?? '',
        isOfficial: false,
        positionData: {
          positionType: 'spot' as const,
          protocolId: positionDetails.protocolId,
          externalIntegrationId: positionDetails.externalIntegrationId,
        },
        perpsMeta: {
          exchange: 'hyperliquid' as const,
          coin: item.asset,
          productKind: 'spot' as const,
          unrealizedPnlUsd: item.pnl,
        },
        balanceUsd: item.usdValue ?? null,
        balance24hChangeUsd: null,
        rateUsd,
        rateUsdChange24h: null,
        rateLastUpdated: null,
        isRateLoading: false as const,
      },
    ];
  });
}

function getPositionDetailsForProvider(providerId: PerpsBalance['providerId']) {
  if (providerId === 'hyperliquid') {
    return {
      protocolId: 'hyperliquid' as const,
      externalIntegrationId: 'hyperliquid-account' as const,
    };
  }

  if (providerId === 'synthetix') {
    return {
      protocolId: 'synthetix' as const,
      externalIntegrationId: 'synthetix-account' as const,
    };
  }

  if (providerId === 'lighter') {
    return {
      protocolId: 'lighter' as const,
      externalIntegrationId: 'lighter-account' as const,
    };
  }

  return null;
}

/** Maps INX allocation source to position type */
const INX_SOURCE_TO_POSITION_TYPE: Record<InxAllocationSource, PositionType> = {
  patronNft: 'inx-patron-nft',
  patronSale: 'inx-patron-sale',
  sonarSale: 'inx-sonar-sale',
  team: 'inx-team',
};

/**
 * Maps INX allocations to position balances.
 * Creates per-source, per-wallet positions for proper grouping and filter attribution.
 * Each allocation source (patronNft, patronSale, sonarSale, team) gets its own position type.
 */
function mapInxDatasetAllocations(
  dataset: InxDataset | undefined,
  addressSet: Set<string>,
  inxPrice: number | null
): ChainPositionChainAssetBalance[] {
  // Require both dataset and token metadata - without real metadata we can't create valid positions
  if (!dataset || !dataset.meta.inxToken || addressSet.size === 0) {
    return [];
  }

  const inxToken = dataset.meta.inxToken;
  const decimals = inxToken.decimals;

  const filteredAllocations = dataset.allocations.filter((a) => {
    try {
      return addressSet.has(parseChainAddress(a.walletAddress));
    } catch {
      return false;
    }
  });

  if (filteredAllocations.length === 0) {
    return [];
  }

  const basePosition = {
    chain: 'ethereum' as const,
    assetAddress: inxToken.address,
    name: inxToken.name,
    symbol: inxToken.symbol,
    imgUrl: inxToken.imgUrl,
    decimals,
    isOfficial: true,
    assetId: inxToken.assetId,
    groupId: { assetId: inxToken.assetId },
  };

  // Group allocations by source and wallet address
  // Key: `${source}:${walletAddress}`, Value: { claimable, locked, allocation }
  type AllocationGroup = {
    claimable: BigNumber;
    locked: BigNumber;
    source: InxAllocationSource;
    walletAddress: string;
  };
  const allocationGroups = new Map<string, AllocationGroup>();

  for (const allocation of filteredAllocations) {
    const key = `${allocation.source}:${allocation.walletAddress}`;
    const existing = allocationGroups.get(key);

    if (existing) {
      existing.claimable = existing.claimable.plus(
        allocation.claimableDecimalAmountInx
      );
      existing.locked = existing.locked.plus(allocation.lockedDecimalAmountInx);
    } else {
      allocationGroups.set(key, {
        claimable: new BigNumber(allocation.claimableDecimalAmountInx),
        locked: new BigNumber(allocation.lockedDecimalAmountInx),
        source: allocation.source,
        walletAddress: allocation.walletAddress,
      });
    }
  }

  const positions: ChainPositionChainAssetBalance[] = [];

  // Create positions for each source/wallet combination
  for (const group of allocationGroups.values()) {
    // `source` comes from the server dataset; a source this client doesn't
    // know yet has no position type mapping, so skip the allocation rather
    // than emitting a position that crashes the type lookups downstream.
    const positionType: PositionType | undefined =
      INX_SOURCE_TO_POSITION_TYPE[group.source];
    if (!positionType) {
      continue;
    }
    // For INX positions, we use the total balance (claimable + locked) since
    // the position type itself indicates whether it's claimable or locked
    const totalBalance = group.claimable.plus(group.locked);

    if (totalBalance.lte(0)) {
      continue;
    }

    const rawBalance = totalBalance.shiftedBy(decimals);
    const balanceUsd =
      inxPrice !== null ? totalBalance.times(inxPrice).toNumber() : null;

    positions.push({
      ...basePosition,
      type: 'position' as const,
      id: makeChainAddressId('ethereum', inxToken.address),
      walletAddress: group.walletAddress,
      rawBalance,
      decimalBalance: totalBalance,
      positionData: {
        positionType,
        protocolId: 'infinex-allocations' as const,
        receiptTokenType: 'token' as const,
      },
      balanceUsd,
      balance24hChangeUsd: null,
      rateUsd: inxPrice,
      rateUsdChange24h: null,
      rateLastUpdated: inxPrice !== null ? Date.now() : null,
      isRateLoading: false as const,
      lstAssetMetadata: null,
    });
  }

  return positions;
}

function mapCollectiblesDatasetCards(
  dataset: CollectiblesDataset
): ChainPositionChainAssetBalance[] {
  return dataset.cards.flatMap((card) => {
    const collectible = card.collectible;
    // Guard the on-chain address before it reaches makeChainAddressId /
    // parseChainAddress: a missing or malformed assetAddress would otherwise
    // throw and take down the whole balances precompute. Skip such a card.
    if (
      !collectible?.assetAddress ||
      !isValidChainAddress(collectible.assetAddress)
    ) {
      return [];
    }

    const decimalBalance = new BigNumber(1);
    const balanceUsd = card.value ? new BigNumber(card.value).toNumber() : null;
    const chainAddressId = makeChainAddressId(
      card.chain,
      collectible.assetAddress
    );

    return {
      type: 'position' as const,
      walletAddress: card.walletAddress,
      chain: card.chain,
      assetAddress: collectible.assetAddress,
      id: chainAddressId,
      groupId: { chain: card.chain, address: collectible.assetAddress },
      name: collectible.name,
      symbol: 'CARD',
      imgUrl: collectible.imageUrl ?? '',
      decimals: 0,
      rawBalance: decimalBalance,
      decimalBalance,
      isOfficial: false,
      lstAssetMetadata: null,
      positionData: {
        positionType: 'collectible' as const,
        protocolId: 'collector-crypt' as const,
        receiptTokenAddress: collectible.assetAddress,
        receiptTokenType: 'nft' as const,
      },
      balanceUsd,
      balance24hChangeUsd: null,
      rateUsd: balanceUsd,
      rateUsdChange24h: null,
      rateLastUpdated: parseTimestampOrFallback(
        card.lastUpdatedAt,
        dataset.generatedAt
      ),
      isRateLoading: false as const,
    };
  });
}

function parseTimestampOrFallback(
  value: string | null | undefined,
  fallback: number
): number {
  if (!value) return fallback;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mapCollectiblesOpenOffersPositions(
  dataset: CollectiblesDataset,
  chainBalancesDataset: ChainBalancesDataset
): ChainPositionChainAssetBalance[] {
  const metadataLookup = indexMetadataV2ByChainAddress(
    chainBalancesDataset.metadataV2 ?? []
  );

  return dataset.openOffers
    .map((openOffers): ChainPositionChainAssetBalance | null => {
      const walletAddress = openOffers.walletAddress?.trim() ?? '';
      if (openOffers.count <= 0 || walletAddress === '') return null;

      const paymentAsset = openOffers.paymentAsset;
      const chainAddressId = makeChainAddressId(
        paymentAsset.chain,
        paymentAsset.address
      );
      const metadata = metadataLookup.get(chainAddressId);
      const staticUsdcMetadata =
        paymentAsset.chain === USDC_SOLANA.chain &&
        paymentAsset.address.toLowerCase() === USDC_SOLANA.address.toLowerCase()
          ? USDC_SOLANA
          : null;
      const member = metadata?.members[paymentAsset.chain];
      const display: {
        name: string;
        symbol: string;
        imgUrl: string;
        decimals: number;
        assetId: string | undefined;
        groupId: AssetGroupId;
        isOfficial: boolean;
      } | null = (() => {
        if (metadata) {
          if (!member) return null;
          return {
            name: metadata.name,
            symbol: metadata.symbol,
            imgUrl: metadata.imgUrl,
            decimals: member.decimals,
            assetId: getAssetIdFromMetadataV2(metadata),
            groupId: metadata.groupId,
            isOfficial: metadata.isOfficial,
          };
        }

        if (!staticUsdcMetadata) return null;
        return {
          name: staticUsdcMetadata.name,
          symbol: staticUsdcMetadata.symbol,
          imgUrl: staticUsdcMetadata.imgUrl,
          decimals: staticUsdcMetadata.decimals,
          assetId: undefined,
          groupId: {
            chain: paymentAsset.chain,
            address: paymentAsset.address,
          },
          isOfficial: false,
        };
      })();
      if (!display) return null;

      const decimalBalance = new BigNumber(openOffers.decimalAmount);
      const rawBalance = decimalBalance.shiftedBy(display.decimals);
      const syntheticReceiptTokenAddress = 'collector-crypt-open-offers';

      return {
        type: 'position' as const,
        walletAddress,
        chain: paymentAsset.chain,
        assetAddress: paymentAsset.address,
        id: chainAddressId,
        groupId: display.groupId,
        assetId: display.assetId,
        name: display.name,
        symbol: display.symbol,
        imgUrl: display.imgUrl,
        decimals: display.decimals,
        rawBalance,
        decimalBalance,
        isOfficial: display.isOfficial,
        lstAssetMetadata: null,
        positionData: {
          positionType: 'collectible' as const,
          protocolId: 'collector-crypt' as const,
          receiptTokenAddress: syntheticReceiptTokenAddress,
          receiptTokenType: 'token' as const,
        },
        balanceUsd: new BigNumber(openOffers.decimalAmountUsd).toNumber(),
        balance24hChangeUsd: null,
        rateUsd: decimalBalance.isZero()
          ? 0
          : new BigNumber(openOffers.decimalAmountUsd)
              .div(decimalBalance)
              .toNumber(),
        rateUsdChange24h: null,
        rateLastUpdated: dataset.generatedAt,
        isRateLoading: false as const,
      };
    })
    .filter(
      (position): position is ChainPositionChainAssetBalance =>
        position !== null
    );
}

function computeRateData(
  price: AssetRate | undefined,
  decimalBalance: BigNumber
): RateData {
  if (!price) {
    return {
      balanceUsd: undefined,
      rateLastUpdated: undefined,
      rateUsd: undefined,
      rateUsdChange24h: undefined,
      balance24hChangeUsd: undefined,
      isRateLoading: true as const,
    };
  }

  const rate = price.prices.usd.rate;

  const balanceUsd = decimalBalance.times(rate);
  const balance24hChangeUsd = calculateBalanceChange(
    balanceUsd.toNumber(),
    price.prices.usd.change24h
  ).balanceChange;

  return {
    balanceUsd: balanceUsd.toNumber(),
    rateLastUpdated: new Date(price.prices.usd.lastUpdated).getTime(),
    rateUsd: rate,
    rateUsdChange24h: price.prices.usd.change24h ?? null,
    balance24hChangeUsd: balance24hChangeUsd,
    isRateLoading: false as const,
  };
}
